import React, { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle2, Circle, ExternalLink } from 'lucide-react'
import styles from './MessageBubble.module.scss'
import { Message } from '../../../../../utils/conversationStorage'
import type { DesktopSuggestion } from '../../dashboardActionsConfig.tsx'
import { useTypewriter } from '../../../../../hooks/useTypewriter.ts'
import { useFloatingAgentMessage } from '../../../../FloatingAgent/FloatingAgentMessageContext.tsx'
import { PORTFOLIO_FIELD_ORDER, PORTFOLIO_FIELD_LABELS, POST_FIELD_ORDER, POST_FIELD_LABELS } from '../../../../../constants/portfolioFields.ts'
import { GUIDE_ACTION_IDS_WITH_SPECIALIST } from '../../../../../constants/agentNames.ts'
import PostPreviewCard from './PostPreviewCard.tsx'

const formatPortfolioValue = (key: string, value: unknown): string => {
  if (value == null || value === '') return ''
  if (key === 'image_uploaded') return value ? 'Lista' : ''
  if (key === 'content') return ''
  if (key === 'type') {
    const s = String(value)
    return s.charAt(0).toUpperCase() + s.slice(1)
  }
  if (key === 'price' && typeof value === 'number') return value.toLocaleString('es-AR')
  if ((key === 'start_date' || key === 'end_date') && typeof value === 'string') {
    try {
      const d = new Date(value)
      return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' })
    } catch {
      return String(value)
    }
  }
  if (key === 'currency_type') {
    const map: Record<string, string> = { ARS: 'Pesos', USD: 'Dólares', EUR: 'Euros' }
    return map[String(value)] || String(value)
  }
  return String(value)
}

interface MessageBubbleProps {
  message: Message
  suggestion?: DesktopSuggestion | null
  onSuggestionAction?: () => void
  onRequestGuide?: () => void
  isLatest?: boolean
  onTypingStateChange?: (messageId: string, isTyping: boolean) => void
  userData?: any
  pendingImagePreview?: string | null
}

const formatCreatedPortfolioPrice = (p: Message['createdPortfolio']) => {
  if (!p?.price) return ''
  const formatted = p.price.toLocaleString('es-AR')
  const currency = p.currencyType === 'ARS' ? 'ARS' : p.currencyType === 'USD' ? 'USD' : p.currencyType === 'EUR' ? 'EUR' : ''
  const unit = p.priceUnit ? ` / ${p.priceUnit}` : ''
  return `${formatted} ${currency}${unit}`
}

const getPortfolioTypeLabel = (type?: 'producto' | 'servicio') =>
  type === 'servicio' ? 'servicio' : 'producto'

const CreatedPortfolioCard: React.FC<{ portfolio: NonNullable<Message['createdPortfolio']> }> = ({ portfolio }) => {
  const navigate = useNavigate()
  const typeLabel = getPortfolioTypeLabel(portfolio.type)
  return (
    <div className={styles.createdPortfolioCard}>
      <div className={styles.createdPortfolioPreview}>
        {portfolio.portfolio_images?.[0]?.image ? (
          <img
            src={portfolio.portfolio_images[0].image}
            alt={portfolio.name}
            className={styles.createdPortfolioImage}
          />
        ) : (
          <div className={styles.createdPortfolioPlaceholder} />
        )}
        <div className={styles.createdPortfolioInfo}>
          <span className={styles.createdPortfolioName}>{portfolio.name}</span>
          {formatCreatedPortfolioPrice(portfolio) && (
            <span className={styles.createdPortfolioPrice}>{formatCreatedPortfolioPrice(portfolio)}</span>
          )}
        </div>
      </div>
      <div className={styles.createdPortfolioActions}>
        <button
          type="button"
          className={styles.createdPortfolioBtn}
          onClick={() => navigate(`/productos/edit/${portfolio.id}`)}
        >
          <ExternalLink size={14} />
          Ver {typeLabel}
        </button>
        {portfolio.user?.id && (
          <button
            type="button"
            className={styles.createdPortfolioBtnSecondary}
            onClick={() => navigate(`/perfil/${portfolio.user!.id}?tab=productos`)}
          >
            Ir a mi portafolio
          </button>
        )}
      </div>
    </div>
  )
}

const CreatedPostCard: React.FC<{ post: NonNullable<Message['createdPost']> }> = ({ post }) => {
  const navigate = useNavigate()
  const imageUrl = post.images?.[0]?.image
  const contentPreview = post.content
    ? post.content.replace(/\n/g, ' ').slice(0, 120) + (post.content.length > 120 ? '…' : '')
    : 'Post publicado'
  const userId = post.user?.id
  return (
    <div className={styles.createdPortfolioCard}>
      <div className={styles.createdPortfolioPreview}>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            className={styles.createdPortfolioImage}
          />
        ) : (
          <div className={styles.createdPortfolioPlaceholder} />
        )}
        <div className={styles.createdPortfolioInfo}>
          <span className={styles.createdPortfolioName}>Post publicado</span>
          <span className={styles.createdPortfolioPrice}>{contentPreview}</span>
        </div>
      </div>
      <div className={styles.createdPortfolioActions}>
        <button
          type="button"
          className={styles.createdPortfolioBtn}
          onClick={() => navigate('/marketplace/explorar')}
        >
          <ExternalLink size={14} />
          Ver en Explorar
        </button>
        {userId && (
          <button
            type="button"
            className={styles.createdPortfolioBtnSecondary}
            onClick={() => navigate(`/perfil/${userId}`)}
          >
            Ir a mi perfil
          </button>
        )}
      </div>
    </div>
  )
}

const normalizeGuideSteps = (content: string): string => {
  return content.replace(/(^|\n)(\d+)\s*\n/g, (_, prefix, n) => `${prefix}Paso ${n}: `)
}

const isGuideMessage = (content: string): boolean => {
  const normalized = normalizeGuideSteps(content)
  return normalized.includes('Paso 1:') || normalized.includes('Paso 1 :')
}

const HIGHLIGHT_TERMS = [
  'profesionales verificados',
  'perfil de Edify',
  'Edify',
  'publicar una obra',
  'publicar tu obra',
  'obras disponibles',
  'ofertas de trabajo',
  'oferta de trabajo',
  'servicio/producto',
  'cotizaciones',
  'presupuestos',
  'propuestas',
  'documentación',
  'oportunidades',
  'tu perfil',
  'servicios',
  'servicio',
  'obras',
  'obra',
  'producto',
  'materiales',
  'comparar',
  'explorar',
  'sección'
]

const hasListFormat = (text: string): boolean => {
  return text.includes('•') || text.includes('\n•') || text.includes('\n-')
}

const formatMessageText = (text: string, messageId: string): React.ReactNode[] => {
  let highlightIndex = 0

  const applyMarkdownBold = (str: string): React.ReactNode[] => {
    const boldRegex = /\*\*([^*]+)\*\*/g
    const parts: React.ReactNode[] = []
    let lastIndex = 0
    let match

    while ((match = boldRegex.exec(str)) !== null) {
      if (match.index > lastIndex) {
        parts.push(str.substring(lastIndex, match.index))
      }
      highlightIndex++
      parts.push(
        <span key={`${messageId}-b-${highlightIndex}`} className={styles.boldText}>
          {match[1]}
        </span>
      )
      lastIndex = match.index + match[0].length
    }

    if (lastIndex < str.length) {
      parts.push(str.substring(lastIndex))
    }

    return parts.length > 0 ? parts : [str]
  }

  const truncateUrl = (url: string, maxLength: number = 50): string => {
    if (url.length <= maxLength) return url

    const parts = url.split('/')
    const lastPart = parts[parts.length - 1] || ''

    if (lastPart && lastPart.includes('.') && lastPart.length < url.length) {
      const extension = lastPart.split('.').pop() || ''
      const nameWithoutExt = lastPart.substring(0, lastPart.lastIndexOf('.'))
      const maxNameLength = maxLength - extension.length - 4

      if (nameWithoutExt.length > 0 && extension.length > 0 && extension.length < 10) {
        if (lastPart.length <= maxLength) {
          return lastPart
        }

        if (nameWithoutExt.length <= maxNameLength) {
          return lastPart
        }

        return `${nameWithoutExt.substring(0, maxNameLength)}...${extension}`
      }
    }

    if (url.length <= maxLength) return url
    return `${url.substring(0, maxLength - 3)}...`
  }

  const isUrl = (str: string): boolean => {
    try {
      new URL(str)
      return true
    } catch {
      const urlPattern = /^(https?:\/\/|www\.)[\w\-._~:/?#[\]@!$&'()*+,;=%]+/i
      return urlPattern.test(str)
    }
  }

  const formatUrl = (url: string): string => {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    if (url.startsWith('www.')) {
      return `https://${url}`
    }
    return `https://${url}`
  }

  const applyLinks = (str: string): React.ReactNode[] => {
    const urlRegex = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+|[^\s<>"']+\.[a-z]{2,}(?:\/[^\s<>"']*)?)/gi
    const parts: React.ReactNode[] = []
    let lastIndex = 0
    let match

    while ((match = urlRegex.exec(str)) !== null) {
      if (match.index > lastIndex) {
        const textBefore = str.substring(lastIndex, match.index)
        parts.push(textBefore)
      }

      let url = match[0]
      const trailingPunctuation = /[.,;:!?]+$/.exec(url)
      let punctuation = ''

      if (trailingPunctuation) {
        punctuation = trailingPunctuation[0]
        url = url.slice(0, -punctuation.length)
      }

      if (isUrl(url)) {
        const fullUrl = formatUrl(url)
        const displayUrl = truncateUrl(url, 50)
        highlightIndex++
        parts.push(
          <a
            key={`${messageId}-url-${highlightIndex}`}
            href={fullUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.messageLink}
            title={url}
          >
            {displayUrl}
          </a>
        )
        if (punctuation) {
          parts.push(punctuation)
        }
      } else {
        parts.push(match[0])
      }

      lastIndex = match.index + match[0].length
    }

    if (lastIndex < str.length) {
      parts.push(str.substring(lastIndex))
    }

    return parts.length > 0 ? parts : [str]
  }

  const applyHighlights = (str: string): React.ReactNode[] => {
    const withLinks = applyLinks(str)

    let result: React.ReactNode[] = withLinks

    result = result.flatMap((part) => {
      if (typeof part === 'string') {
        return applyMarkdownBold(part)
      }
      return part
    })

    HIGHLIGHT_TERMS.forEach(term => {
      result = result.flatMap((part) => {
        if (typeof part !== 'string') return part

        const regex = new RegExp(`(${term})`, 'gi')
        const parts = part.split(regex)

        return parts.map((p) => {
          if (p.toLowerCase() === term.toLowerCase()) {
            highlightIndex++
            return <span key={`${messageId}-hl-${highlightIndex}`} className={styles.highlight}>{p}</span>
          }
          return p
        })
      })
    })

    return result
  }

  if (hasListFormat(text)) {
    const lines = text.split('\n')
    return lines.map((line, lineIndex) => {
      const trimmedLine = line.trim()
      if (!trimmedLine) return null

      const isBullet = trimmedLine.startsWith('•') || trimmedLine.startsWith('-')
      const content = isBullet ? trimmedLine.substring(1).trim() : trimmedLine

      return (
        <span
          key={`${messageId}-l-${lineIndex}`}
          className={isBullet ? styles.bulletItem : styles.sentence}
        >
          {isBullet && <span className={styles.bullet}>•</span>}
          {applyHighlights(content)}
        </span>
      )
    }).filter(Boolean) as React.ReactNode[]
  }

  const sentences = text.split(/(?<=\.)\s+(?=[A-Z¿])|(?<=\?)\s+(?=[A-Z¿])/)

  return sentences.map((sentence, sentenceIndex) => {
    return (
      <span key={`${messageId}-s-${sentenceIndex}`} className={styles.sentence}>
        {applyHighlights(sentence)}
      </span>
    )
  })
}

const parsePortfolioProgress = (llmRawResponse?: string) => {
  if (!llmRawResponse) return null
  try {
    const parsed = JSON.parse(llmRawResponse)
    const missingFields: string[] = parsed.missingFields || []
    const collectedData: Record<string, any> = parsed.collectedData || {}
    const intent = collectedData?.intent

    if (intent === 'create_post') {
      const items: { key: string; label: string; done: boolean; value?: string }[] = []
      for (const key of POST_FIELD_ORDER) {
        const hasValue =
          key === 'content'
            ? collectedData[key] != null && String(collectedData[key]).trim() !== ''
            : collectedData[key] === true
        const valueStr = hasValue ? formatPortfolioValue(key, collectedData[key]) : undefined
        items.push({
          key,
          label: POST_FIELD_LABELS[key] || key,
          done: hasValue,
          value: valueStr
        })
      }
      return { items, imagePreviewUrl: parsed.imagePreviewUrl as string | undefined }
    }

    if (missingFields.length === 0 && Object.keys(collectedData).filter(k => k !== 'intent').length === 0) return null
    const items: { key: string; label: string; done: boolean; value?: string }[] = []
    for (const key of PORTFOLIO_FIELD_ORDER) {
      const hasValue = collectedData[key] != null && collectedData[key] !== ''
      const isMissing = missingFields.includes(key)
      if (hasValue || isMissing) {
        const valueStr = hasValue ? formatPortfolioValue(key, collectedData[key]) : undefined
        items.push({
          key,
          label: PORTFOLIO_FIELD_LABELS[key] || key,
          done: hasValue,
          value: valueStr
        })
      }
    }
    if (items.length === 0) return null
    return { items, imagePreviewUrl: parsed.imagePreviewUrl as string | undefined }
  } catch {
    return null
  }
}

const parseGuideContent = (content: string) => {
  const firstStepMatch = content.match(/Paso\s*1\s*:/)

  if (!firstStepMatch) {
    return { intro: content, steps: [], outro: '', partialStep: '' }
  }

  const introEndIndex = content.indexOf(firstStepMatch[0])
  const intro = content.substring(0, introEndIndex).trim()

  const stepsSection = content.substring(introEndIndex)
  const steps: string[] = []
  let partialStep = ''
  let outro = ''

  const lastQuestionIndex = stepsSection.lastIndexOf('¿')
  const stepsOnlySection = lastQuestionIndex > 0
    ? stepsSection.substring(0, lastQuestionIndex)
    : stepsSection

  if (lastQuestionIndex > 0) {
    outro = stepsSection.substring(lastQuestionIndex).trim()
  }

  const stepParts = stepsOnlySection.split(/(?=Paso\s*\d+\s*:)/)

  stepParts.forEach((part, index) => {
    if (part.match(/^Paso\s*\d+\s*:/)) {
      const isLastPart = index === stepParts.length - 1

      const stepContent = part.replace(/^Paso\s*\d+\s*:/, '').trim()
      const hasNewlineAfterContent = part.includes('\n') && stepContent.length > 0
      const isFollowedByNextStep = !isLastPart && stepParts[index + 1]?.match(/^Paso\s*\d+\s*:/)

      if (isFollowedByNextStep || (hasNewlineAfterContent && !isLastPart)) {
        steps.push(part.trim().replace(/\n+/g, ' '))
      } else if (isLastPart) {
        partialStep = part.trim()
      }
    }
  })

  return { intro, steps, outro, partialStep }
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  suggestion,
  onSuggestionAction,
  onRequestGuide,
  isLatest = false,
  onTypingStateChange,
  userData,
  pendingImagePreview = null
}) => {
  const isUser = message.role === 'user'
  const isGuide = !isUser && isGuideMessage(message.content)
  const shouldAnimate = !isUser && isLatest
  const contentForDisplay =
    !isUser && message.createdPortfolio?.type === 'servicio'
      ? message.content.replace(/\bproducto\b/gi, 'servicio')
      : message.content
  const { displayedText, isTyping } = useTypewriter(contentForDisplay, shouldAnimate)
  const inFloatingAgent = useFloatingAgentMessage()

  const previousTypingState = useRef<boolean | undefined>(undefined)
  const callbackRef = useRef(onTypingStateChange)

  useEffect(() => {
    callbackRef.current = onTypingStateChange
  }, [onTypingStateChange])

  useEffect(() => {
    if (previousTypingState.current !== isTyping) {
      previousTypingState.current = isTyping
      if (callbackRef.current) {
        callbackRef.current(message.id, isTyping)
      }
    }
  }, [isTyping, message.id])

  if (isGuide) {
    const guideContent = parseGuideContent(normalizeGuideSteps(displayedText))
    const { intro, steps, outro, partialStep } = guideContent

    const showActionButton = !isUser && suggestion && message.actionId === suggestion.id && onSuggestionAction && !isTyping
    const hasSpecialist = message.actionId && GUIDE_ACTION_IDS_WITH_SPECIALIST.includes(message.actionId)
    const showGuideRequestButton = hasSpecialist && onRequestGuide && !isTyping

    return (
      <div
        className={`${styles.messageBubble} ${styles.assistantMessage} ${styles.guideMessage} ${isTyping ? styles.typing : ''} ${inFloatingAgent ? styles.inFloatingAgent : ''}`}
      >
        <div className={styles.messageContent}>
          {intro && (
            <p className={styles.guideIntro}>{intro}</p>
          )}

          <div className={styles.guideSteps}>
            {steps.map((step, index) => (
              <div key={index} className={styles.guideStep}>
                <span className={styles.stepContent}>{step}</span>
              </div>
            ))}
            {partialStep && (
              <div className={styles.guideStep}>
                <span className={styles.stepContent}>
                  {partialStep.replace(/\n/g, ' ')}
                  {isTyping && <span className={styles.cursor}>|</span>}
                </span>
              </div>
            )}
          </div>

          {outro && (
            <p className={styles.guideOutro}>
              {outro}
              {isTyping && <span className={styles.cursor}>|</span>}
            </p>
          )}

          {!intro && steps.length === 0 && !partialStep && displayedText && (
            <p className={styles.guideIntro}>
              {displayedText}
              {isTyping && <span className={styles.cursor}>|</span>}
            </p>
          )}

          {(showActionButton || showGuideRequestButton) && (
            <div className={styles.guideActionButtons}>
               {showGuideRequestButton && (
                <button
                  type="button"
                  className={styles.guideRequestButton}
                  onClick={onRequestGuide}
                  aria-label="Guíame paso a paso"
                >
                  Guíame paso a paso
                </button>
              )}
              {showActionButton && (
                <button
                  type="button"
                  className={styles.guideActionButton}
                  onClick={onSuggestionAction}
                  aria-label={suggestion?.label}
                >
                  Comenzar →
                </button>
              )}           
            </div>
          )}
        </div>
      </div>
    )
  }

  const showActionButton = !isUser && suggestion && message.actionId === suggestion.id && onSuggestionAction && !isTyping

  const textToShow = isUser ? message.content : displayedText
  const portfolioProgress = !isUser ? parsePortfolioProgress(message.llmRawResponse) : null
  const portfolioItems = portfolioProgress?.items ?? null
  const summaryImageUrl = portfolioProgress?.imagePreviewUrl
  const createdPortfolio = message.createdPortfolio

  let postPreviewContent: string | null = null
  let postPreviewImageUrl: string | null = null
  let textWhenPostPreview = textToShow
  if (!isUser && message.llmRawResponse && message.actionId === 'guardar-post') {
    try {
      const parsed = JSON.parse(message.llmRawResponse)
      const content = parsed?.collectedData?.content
      if (content) {
        postPreviewContent = content
        postPreviewImageUrl = parsed?.imagePreviewUrl ?? pendingImagePreview ?? null
        const questionPart = message.content
          ?.replace(content, '')
          ?.replace(/Acá va la propuesta:\s*\n*/i, '')
          ?.replace(/\n{2,}/g, '\n')
          ?.trim()
        if (questionPart) textWhenPostPreview = questionPart
      }
    } catch {}
  }

  return (
    <div
      className={`${styles.messageBubble} ${
        isUser ? styles.userMessage : styles.assistantMessage
      } ${isTyping ? styles.typing : ''} ${inFloatingAgent ? styles.inFloatingAgent : ''}`}
    >
      <div className={styles.messageContent}>
        {postPreviewContent && (
          <PostPreviewCard
            content={postPreviewContent}
            userData={userData}
            imagePreviewUrl={postPreviewImageUrl}
          />
        )}
        {portfolioItems && portfolioItems.length > 0 && (
          <div className={styles.portfolioProgress}>
            <div
              className={styles.portfolioProgressBar}
              style={{
                width: `${(portfolioItems.filter(i => i.done).length / portfolioItems.length) * 100}%`
              }}
            />
            <div className={styles.portfolioProgressHeader}>
              <span className={styles.portfolioProgressCount}>
                {portfolioItems.filter(i => i.done).length}/{portfolioItems.length} completados
              </span>
            </div>
            <div className={styles.portfolioProgressList}>
              {portfolioItems.map((item) => (
                <div
                  key={item.key}
                  className={`${styles.portfolioProgressItem} ${item.done ? styles.portfolioItemDone : styles.portfolioItemPending}`}
                >
                  {item.done ? (
                    <CheckCircle2 size={18} className={styles.portfolioIconDone} aria-hidden />
                  ) : (
                    <Circle size={18} className={styles.portfolioIconPending} aria-hidden />
                  )}
                  <span className={styles.portfolioLabel}>{item.label}</span>
                  {item.done && item.value && item.key !== 'image_uploaded' && (
                    <span className={styles.portfolioValue} title={item.value}>
                      {item.value.length > 20 ? `${item.value.slice(0, 18)}…` : item.value}
                    </span>
                  )}
                  {item.done && item.key === 'image_uploaded' && summaryImageUrl && (
                    <img
                      src={summaryImageUrl}
                      alt=""
                      className={styles.portfolioSummaryImage}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className={styles.messageText}>
          {isUser ? message.content : formatMessageText(postPreviewContent ? textWhenPostPreview : textToShow, message.id)}
          {isTyping && <span className={styles.cursor}>|</span>}
        </div>

        {createdPortfolio && !isUser && <CreatedPortfolioCard portfolio={createdPortfolio} />}

        {message.createdPost && !isUser && <CreatedPostCard post={message.createdPost} />}

        {showActionButton && (
          <button
            type="button"
            className={styles.actionLink}
            onClick={onSuggestionAction}
            aria-label={suggestion.label}
          >
            {suggestion.label} →
          </button>
        )}
      </div>
    </div>
  )
}

export default MessageBubble
